/**
 * Hook that combines filter view store, API hooks, and URL state
 * Provides centralized logic for managing both filters and filter views
 */

import { useCallback, useMemo, useState, useRef, useEffect } from 'react';

import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigationType, useSearchParams } from 'react-router';
import type { StoreApi, UseBoundStore } from 'zustand';

import {
  useGetFilterViews,
  useCreateFilterView,
  useUpdateFilterView,
  useDeleteFilterView,
  type FilterView,
} from '@/api/REST/filterViews';
import {
  parseFilters,
  serializeFilters,
  removeClause,
  updateClause,
  canonicalizeFilterSet,
  type FilterSet,
  type FilterClause,
} from '@/lib/filter-dsl';
import type { FilterViewStoreState } from '@/stores/filterViewUtils/createFilterViewStore';

interface UseFilterManagerParams {
  /**
   * The store hook for this page (e.g., useProjectsFilterViewStore)
   */
  useStore: UseBoundStore<StoreApi<FilterViewStoreState>>;
}

/**
 * Manages both filters and filter views for a page
 * Combines store state, API data, and URL params into a unified interface
 */
export function useFilterManager({ useStore }: UseFilterManagerParams) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigationType = useNavigationType();
  const location = useLocation();
  const store = useStore();
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isCreatingView, setIsCreatingView] = useState(false);
  const [isSavingAsNewView, setIsSavingAsNewView] = useState(false);
  const { t } = useTranslation('views');
  const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const { pageIdentifier, selectedViewId, setSelectedViewId } = store;

  // Track if we've loaded default view on this mount
  const hasLoadedDefaultView = useRef(false);

  // Track user interactions that should prevent auto-loading default view
  const userExplicitlyClickedAll = useRef(false);
  const userIsModifyingFilters = useRef(false);
  const userIsSelectingView = useRef(false);

  // Track previous filter state to detect navigation-induced transitions
  // Use null to distinguish "not yet set" from an empty filter string.
  const prevFiltersRef = useRef<string | null>(null);
  const userClearedFiltersRef = useRef(false);

  // Helper function to canonicalize filter parameters
  // Parses and re-serializes with consistent ordering for comparison
  const canonicalizeFilters = useCallback(
    (filterParams: string): string => {
      if (!filterParams) {
        return '';
      }
      try {
        // Parse the filter string
        const params = new URLSearchParams();
        params.set('filters', filterParams);
        const parsed = parseFilters(params);

        return canonicalizeFilterSet(parsed);
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn(
            '[useFilterManager] Failed to parse filter params for canonicalization.',
            {
              filterParams,
              error,
            },
          );
        }
        // If parsing fails, return original
        return filterParams;
      }
    },
    [canonicalizeFilterSet],
  );

  // Parse current filters from URL - use stable string reference
  // Create fresh URLSearchParams from current URL to ensure we parse the latest filters
  const filterSet = useMemo(
    () => parseFilters(new URLSearchParams(window.location.search)),
    [searchParams, isTransitioning],
  );

  // Get current state from URL and store
  // IMPORTANT: Use a canonical serialized representation for comparisons
  // This ensures that filters with the same clauses in different orders are treated as equal
  // Example: is_active:is(true)|customer:is(1508+A/S) === customer:is(1508+A/S)|is_active:is(true)
  const currentFilters = useMemo(() => {
    return canonicalizeFilterSet(filterSet);
  }, [canonicalizeFilterSet, filterSet]);

  // API hooks
  const { data: filterViews = [], isLoading } =
    useGetFilterViews(pageIdentifier);
  const createView = useCreateFilterView(pageIdentifier);
  const updateView = useUpdateFilterView(pageIdentifier);
  const deleteViewMutation = useDeleteFilterView(pageIdentifier);

  const getPreferredMatchingView = useCallback(
    (filters: string): FilterView | null => {
      if (!filters) {
        return null;
      }

      const matchingViews = filterViews.filter(
        (view) => canonicalizeFilters(view.filterParameters) === filters,
      );

      if (matchingViews.length === 0) {
        return null;
      }

      const defaultMatch = matchingViews.find((view) => view.isDefault);
      if (defaultMatch) {
        return defaultMatch;
      }

      return matchingViews.reduce((best, candidate) => {
        const bestOrder =
          best.sortOrder === undefined
            ? Number.POSITIVE_INFINITY
            : best.sortOrder;
        const candidateOrder =
          candidate.sortOrder === undefined
            ? Number.POSITIVE_INFINITY
            : candidate.sortOrder;

        if (candidateOrder !== bestOrder) {
          return candidateOrder < bestOrder ? candidate : best;
        }

        return candidate.filterViewId < best.filterViewId ? candidate : best;
      });
    },
    [canonicalizeFilters, filterViews],
  );

  // Find current view - either from explicit selection or by matching filters
  // Keep the view highlighted even when filters are modified (show asterisk for unsaved changes)
  const currentView = useMemo(() => {
    // If a view is explicitly selected, use it even if filters don't match
    if (selectedViewId) {
      return filterViews.find((v) => v.filterViewId === selectedViewId) || null;
    }

    // Otherwise, auto-select a view if filters match exactly.
    // Use canonical comparison to handle different filter ordering.
    if (currentFilters) {
      return getPreferredMatchingView(currentFilters);
    }

    return null;
  }, [
    currentFilters,
    filterViews,
    selectedViewId,
    canonicalizeFilters,
    getPreferredMatchingView,
  ]);

  const currentViewId = currentView?.filterViewId || null;

  // Compute derived state
  // Don't show unsaved changes during view transitions to prevent flickering
  // Use canonical comparison to handle different filter ordering
  const hasUnsavedChanges =
    currentView && !isTransitioning
      ? canonicalizeFilters(currentView.filterParameters) !== currentFilters
      : false;

  const hasFilters = currentFilters.length > 0;

  // === Filter Manipulation Actions ===

  /**
   * Updates the URL with a new FilterSet
   */
  const updateFilters = useCallback(
    (newFilterSet: FilterSet) => {
      const filtersString = serializeFilters(newFilterSet);

      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (filtersString) {
            // Set the filters parameter - URLSearchParams will handle encoding
            params.set('filters', filtersString);
          } else {
            params.delete('filters');
          }
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  /**
   * Removes a filter by facet key
   */
  const removeFilter = useCallback(
    (facetKey: string) => {
      userIsModifyingFilters.current = true;
      const updated = removeClause(filterSet, facetKey);
      updateFilters(updated);
    },
    [filterSet, updateFilters],
  );

  /**
   * Updates or creates a filter clause
   */
  const updateFilter = useCallback(
    (clause: FilterClause) => {
      userIsModifyingFilters.current = true;
      const updated = updateClause(filterSet, clause);
      updateFilters(updated);
    },
    [filterSet, updateFilters],
  );

  // === View Management Actions ===

  const selectView = useCallback(
    (viewId: string, filterParameters?: string) => {
      userIsSelectingView.current = true;
      // If filterParameters is provided, use it directly (for newly created views)
      // Otherwise, find the view in the array
      let viewFilterParams = filterParameters;
      if (viewFilterParams === undefined) {
        const view = filterViews.find((v) => v.filterViewId === viewId);
        if (!view) {
          return;
        }
        viewFilterParams = view.filterParameters;
      }

      // Set transitioning flag and selected view ID synchronously BEFORE navigation
      // flushSync forces React to apply these updates immediately, not in a batch
      // This ensures the UI highlights the new view before the URL navigation completes
      flushSync(() => {
        setIsTransitioning(true);
        setSelectedViewId(viewId);
      });

      // Use setSearchParams to update URL with the view's filters
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (viewFilterParams) {
            // Set the filters parameter - URLSearchParams will handle encoding
            params.set('filters', viewFilterParams);
          } else {
            params.delete('filters');
          }
          return params;
        },
        { replace: false },
      );

      // Clear any existing timeout before setting a new one
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }

      // Clear transitioning flag after a brief delay
      transitionTimeoutRef.current = setTimeout(() => {
        setIsTransitioning(false);
        transitionTimeoutRef.current = null;
      }, 100);
    },
    [filterViews, setSearchParams, setSelectedViewId],
  );

  // Helper to generate unique view name
  const generateUniqueViewName = useCallback(() => {
    const baseName = t('NewViewDefaultName');
    const existingNames = filterViews.map((v) => v.name);

    if (!existingNames.includes(baseName)) {
      return baseName;
    }

    let counter = 1;
    while (existingNames.includes(`${baseName} (${counter})`)) {
      counter++;
    }

    return `${baseName} (${counter})`;
  }, [filterViews, t]);

  const saveCurrentView = async () => {
    if (!currentView) return;

    await updateView.mutateAsync({
      id: currentView.filterViewId,
      data: {
        name: currentView.name,
        filterParameters: currentFilters,
        sortOrder: currentView.sortOrder,
        isDefault: currentView.isDefault ?? false,
      },
    });
  };

  const saveAsNewView = async (name?: string) => {
    // Generate unique name if not provided
    const viewName = name || generateUniqueViewName();

    // get the highest sort order
    const highestSortOrder = filterViews.reduce(
      (max, view) => Math.max(max, view.sortOrder || 0),
      0,
    );
    const newView = await createView.mutateAsync({
      name: viewName,
      filterParameters: currentFilters,
      sortOrder: highestSortOrder + 1,
    });

    // Set the newly created view as selected
    setSelectedViewId(newView.filterViewId);

    return newView;
  };

  const createEmptyView = async (name?: string) => {
    // Generate unique name if not provided
    const viewName = name || generateUniqueViewName();

    // get the highest sort order
    const highestSortOrder = filterViews.reduce(
      (max, view) => Math.max(max, view.sortOrder || 0),
      0,
    );
    const newView = await createView.mutateAsync({
      name: viewName,
      filterParameters: '', // Empty string for views with no filters
      sortOrder: highestSortOrder + 1,
    });

    // Set the newly created view as selected
    setSelectedViewId(newView.filterViewId);

    return newView;
  };

  const duplicateView = async (view: FilterView) => {
    // Generate a name like "View name (copy)" or "View name (copy 2)"
    const baseName = `${view.name} (${t('NewViewDuplicateSuffix')})`;
    const existingNames = filterViews.map((v) => v.name);

    let duplicateName = baseName;
    if (existingNames.includes(baseName)) {
      let counter = 2;
      while (
        existingNames.includes(
          `${view.name} (${t('NewViewDuplicateSuffix')} ${counter})`,
        )
      ) {
        counter++;
      }
      duplicateName = `${view.name} (${t('NewViewDuplicateSuffix')} ${counter})`;
    }

    // Get the highest sort order to place duplicate at the end
    const highestSortOrder = filterViews.reduce(
      (max, v) => Math.max(max, v.sortOrder || 0),
      0,
    );

    const newView = await createView.mutateAsync({
      name: duplicateName,
      filterParameters: view.filterParameters,
      sortOrder: highestSortOrder + 1,
    });

    // Set the newly created view as selected
    setSelectedViewId(newView.filterViewId);

    return newView;
  };

  // Trigger functions for view creation flows
  const startCreatingView = () => setIsCreatingView(true);
  const endCreatingView = () => setIsCreatingView(false);
  const startSavingAsNewView = () => setIsSavingAsNewView(true);
  const endSavingAsNewView = () => setIsSavingAsNewView(false);

  const deleteView = async (viewId: string) => {
    await deleteViewMutation.mutateAsync(viewId);
    // If the deleted view was selected, clear the selection
    if (selectedViewId === viewId) {
      setSelectedViewId(null);
    }
  };

  const resetFilters = () => {
    if (currentView) {
      userIsModifyingFilters.current = true;
      // Reset to saved view's filters
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (currentView.filterParameters) {
            // Set the filters parameter - URLSearchParams will handle encoding
            params.set('filters', currentView.filterParameters);
          } else {
            params.delete('filters');
          }
          return params;
        },
        { replace: true },
      );
    } else {
      // Treat as explicit "All" action when clearing without a view selected.
      userExplicitlyClickedAll.current = true;
      hasLoadedDefaultView.current = true;
      userIsModifyingFilters.current = true;

      // Clear all filters and view selection
      setSelectedViewId(null);
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.delete('filters');
          return params;
        },
        { replace: true },
      );
    }
  };

  const clearFilters = () => {
    // Clear all filters but keep view selection
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.delete('filters');
        return params;
      },
      { replace: false },
    );
  };

  const clearViewAndFilterSelection = () => {
    // Clear view selection and filters
    setSelectedViewId(null);

    // Mark that user explicitly clicked "All" - prevents default view loading
    userExplicitlyClickedAll.current = true;
    hasLoadedDefaultView.current = true;

    // Mark as user modification so transition detection doesn't reset the flag
    userIsModifyingFilters.current = true;

    clearFilters();
  };

  // Reset flags on mount to allow default view loading on page navigation
  useEffect(() => {
    hasLoadedDefaultView.current = false;
    userExplicitlyClickedAll.current = false;
    userIsModifyingFilters.current = false;

    // Cleanup timeout on unmount
    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
    };
  }, []); // Empty deps = run only on mount

  // Initial selection sync when URL filters are already present.
  useEffect(() => {
    if (isLoading) {
      return;
    }

    const prevFilters = prevFiltersRef.current;
    if (prevFilters !== null) {
      return;
    }

    if (!currentFilters) {
      return;
    }

    const preferredView = getPreferredMatchingView(currentFilters);
    if (preferredView && selectedViewId !== preferredView.filterViewId) {
      setSelectedViewId(preferredView.filterViewId);
    }
  }, [
    currentFilters,
    getPreferredMatchingView,
    isLoading,
    selectedViewId,
    setSelectedViewId,
  ]);

  // Track transitions between empty and non-empty filters.
  useEffect(() => {
    if (isLoading) {
      return;
    }

    const prevFilters = prevFiltersRef.current;
    if (prevFilters === null) {
      return;
    }

    const hadFiltersBeforeAndNowEmpty = prevFilters && !currentFilters;
    const hadNoFiltersBeforeAndNowHas = !prevFilters && currentFilters;
    const filtersChanged = prevFilters !== currentFilters;

    if (hadFiltersBeforeAndNowEmpty && userIsModifyingFilters.current) {
      userClearedFiltersRef.current = true;
    }

    // Detect transition: had filters → now empty (e.g., clicked Portfolio link)
    // If not caused by user action, reset flags to allow default view loading
    if (hadFiltersBeforeAndNowEmpty) {
      if (!userIsModifyingFilters.current && !userIsSelectingView.current) {
        // External navigation (e.g., clicked Portfolio link)
        // Reset flags to allow default view loading
        userExplicitlyClickedAll.current = false;
        hasLoadedDefaultView.current = false;
        if (selectedViewId) {
          setSelectedViewId(null);
        }
      }
      // Always reset the modifying flag after processing transition
      userIsModifyingFilters.current = false;
    }

    // Detect transition: no filters → has filters (e.g., clicked a view or added filter)
    // This means user moved away from "All", so reset the flag
    if (hadNoFiltersBeforeAndNowHas) {
      userExplicitlyClickedAll.current = false;
    }

    if (filtersChanged && userIsSelectingView.current) {
      userIsSelectingView.current = false;
    }
  }, [currentFilters, isLoading, selectedViewId, setSelectedViewId]);

  // Sync view selection when filters change due to external navigation.
  useEffect(() => {
    if (isLoading) {
      return;
    }

    const prevFilters = prevFiltersRef.current;
    if (prevFilters === null) {
      return;
    }
    const filtersChanged = prevFilters !== currentFilters;

    if (!filtersChanged || !currentFilters) {
      return;
    }

    if (userIsModifyingFilters.current) {
      userIsModifyingFilters.current = false;
      return;
    }

    const matchingView = getPreferredMatchingView(currentFilters);

    if (matchingView) {
      if (selectedViewId !== matchingView.filterViewId) {
        setSelectedViewId(matchingView.filterViewId);
      }
      return;
    }

    if (selectedViewId) {
      const selectedView = filterViews.find(
        (view) => view.filterViewId === selectedViewId,
      );
      const selectedMatches =
        selectedView &&
        canonicalizeFilters(selectedView.filterParameters) === currentFilters;

      if (!selectedMatches) {
        setSelectedViewId(null);
      }
    }
  }, [
    currentFilters,
    filterViews,
    getPreferredMatchingView,
    isLoading,
    selectedViewId,
    setSelectedViewId,
  ]);

  // Auto-load default view when navigating to page with no filters.
  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (hasLoadedDefaultView.current) {
      return;
    }

    // Browser back/forward (POP with a non-initial key) should restore the
    // exact URL state the user left, not redirect to the default view.
    if (navigationType === 'POP' && location.key !== 'default') {
      if (selectedViewId) {
        const selectedView = filterViews.find(
          (view) => view.filterViewId === selectedViewId,
        );
        const selectedMatches =
          selectedView &&
          canonicalizeFilters(selectedView.filterParameters) === currentFilters;

        if (!selectedMatches) {
          setSelectedViewId(null);
          return;
        }
      }

      hasLoadedDefaultView.current = true;
      return;
    }

    const prevFilters = prevFiltersRef.current;
    const hadFiltersBeforeAndNowEmpty = prevFilters && !currentFilters;
    const clearedFiltersByUser =
      hadFiltersBeforeAndNowEmpty && userClearedFiltersRef.current;

    // Handle two cases:
    // 1. URL has no filters - load default view
    // 2. URL has filters but no view selected - check if it matches default
    if (!currentFilters) {
      if (clearedFiltersByUser || userExplicitlyClickedAll.current) {
        hasLoadedDefaultView.current = true;
        userClearedFiltersRef.current = false;
        return;
      }

      if (selectedViewId) {
        const selectedView = filterViews.find(
          (view) => view.filterViewId === selectedViewId,
        );
        const selectedMatches =
          selectedView &&
          canonicalizeFilters(selectedView.filterParameters) === currentFilters;

        // Keep selection for explicit empty views.
        if (selectedMatches) {
          hasLoadedDefaultView.current = true;
          userClearedFiltersRef.current = false;
          return;
        }

        // Clear any stale selectedViewId from store when URL has no filters
        // This handles: user had View B selected, clicks Portfolio link
        setSelectedViewId(null);
        return; // Let effect run again after clearing
      }

      // Try to load default view if one exists
      const defaultView = filterViews.find((v) => v.isDefault);
      if (defaultView) {
        hasLoadedDefaultView.current = true;
        userClearedFiltersRef.current = false;
        selectView(defaultView.filterViewId, defaultView.filterParameters);
      } else {
        // No default view, mark as loaded to prevent repeated checks
        hasLoadedDefaultView.current = true;
        userClearedFiltersRef.current = false;
      }
    } else if (!selectedViewId && currentFilters) {
      // URL has filters but no view is explicitly selected in store
      // Check if filters match the default view (from Portfolio nav link)
      const defaultView = filterViews.find((v) => v.isDefault);
      if (
        defaultView &&
        canonicalizeFilters(defaultView.filterParameters) === currentFilters
      ) {
        // Filters match default view - set it as selected
        hasLoadedDefaultView.current = true;
        userClearedFiltersRef.current = false;
        setSelectedViewId(defaultView.filterViewId);
      }
    }
  }, [
    canonicalizeFilters,
    filterViews,
    isLoading,
    currentFilters,
    location.key,
    navigationType,
    selectedViewId,
    selectView,
    setSelectedViewId,
  ]);

  // Track previous filters after all effects run for the next cycle.
  useEffect(() => {
    prevFiltersRef.current = currentFilters;
  }, [currentFilters]);

  return {
    // Filter State
    filterSet,
    currentFilters,
    hasFilters,

    // Filter Actions
    updateFilters,
    removeFilter,
    updateFilter,

    // View State
    filterViews,
    currentView,
    currentViewId,
    hasUnsavedChanges,
    filterPanelOpen: store.filterPanelOpen,
    isLoading,
    isTransitioning,
    isCreatingView,
    isSavingAsNewView,

    // View Actions
    selectView,
    saveCurrentView,
    saveAsNewView,
    createEmptyView,
    duplicateView,
    deleteView,
    resetFilters,
    clearFilters,
    clearViewAndFilterSelection,
    toggleFilterPanel: store.toggleFilterPanel,
    setFilterPanelOpen: store.setFilterPanelOpen,
    startCreatingView,
    endCreatingView,
    startSavingAsNewView,
    endSavingAsNewView,

    // Mutation hooks (for passing to components that need direct access)
    createViewMutation: createView,
    updateViewMutation: updateView,
    deleteViewMutation,

    // Mutation states
    isSaving: updateView.isPending || createView.isPending,
    isDeleting: deleteViewMutation.isPending,
  };
}
